import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import './index.scss';
import ImagePreviewModal from '../../ImagePreviewModal';
import Button from '../../Button';
import ProgressBar from '../../ProgressBar';

export default function VideoFrameExtractor() {
  const [videoUrl, setVideoUrl] = useState('');
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef();
  const inputRef = useRef();
  const overlayRef = useRef();
  const selectionRef = useRef();

  // 直接在组件中管理选区状态
  const [selecting, setSelecting] = useState(false);
  const [selection, setSelection] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeCorner, setResizeCorner] = useState(null);

  // 功能2：抽帧方式相关状态
  const [extractMode, setExtractMode] = useState('manual'); // manual, interval, keyframe, custom, range
  const [extractProgress, setExtractProgress] = useState(0);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedFrames, setExtractedFrames] = useState([]);
  
  // 等间隔抽帧参数
  const [intervalType, setIntervalType] = useState('seconds'); // seconds, frames
  const [intervalValue, setIntervalValue] = useState(1);
  
  // 自定义时间点抽帧
  const [customTimes, setCustomTimes] = useState('');
  
  // 区间抽帧参数
  const [rangeStart, setRangeStart] = useState(0);
  const [rangeEnd, setRangeEnd] = useState(0);

  // 功能3：帧预览与管理相关状态
  const [selectedFrames, setSelectedFrames] = useState(new Set());
  const [previewFrame, setPreviewFrame] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [frameSortBy, setFrameSortBy] = useState('time'); // time, name, id
  const [frameSortOrder, setFrameSortOrder] = useState('asc'); // asc, desc
  const [frameDisplayMode, setFrameDisplayMode] = useState('grid'); // grid, list
  const [frameRenameId, setFrameRenameId] = useState(null);
  const [frameRenameValue, setFrameRenameValue] = useState('');

  // 使用ref存储当前状态，避免闭包问题
  const currentState = useRef({
    selecting: false,
    dragging: false,
    resizing: false,
    selection: null,
    dragOffset: { x: 0, y: 0 },
    resizeCorner: null
  });

  // 同步状态到ref
  useEffect(() => {
    currentState.current = { selecting, dragging, resizing, selection, dragOffset, resizeCorner };
  }, [selecting, dragging, resizing, selection, dragOffset, resizeCorner]);

  // 视频加载完成后设置区间抽帧的结束时间
  useEffect(() => {
    if (duration > 0) {
      setRangeEnd(duration);
    }
  }, [duration]);

  // 使用useLayoutEffect确保DOM同步更新
  useLayoutEffect(() => {
    if (selection && selectionRef.current) {
      const width = Math.abs(selection.endX - selection.startX);
      const height = Math.abs(selection.endY - selection.startY);
      
      if (width > 5 && height > 5) {
        const left = Math.min(selection.startX, selection.endX);
        const top = Math.min(selection.startY, selection.endY);
        
        selectionRef.current.style.left = `${left}px`;
        selectionRef.current.style.top = `${top}px`;
        selectionRef.current.style.width = `${width}px`;
        selectionRef.current.style.height = `${height}px`;
        selectionRef.current.style.display = 'block';
      } else {
        selectionRef.current.style.display = 'none';
      }
    } else if (selectionRef.current) {
      selectionRef.current.style.display = 'none';
    }
  }, [selection]);

  // 监听全局mouseup，防止鼠标移出视频区域后依然处于选择/拖动状态
  useEffect(() => {
    const handleWindowMouseUp = () => {
      setSelecting(false);
      setDragging(false);
      setResizing(false);
      setResizeCorner(null);
    };
    window.addEventListener('mouseup', handleWindowMouseUp);
    return () => window.removeEventListener('mouseup', handleWindowMouseUp);
  }, []);

  // 处理本地文件上传
  const handleFile = e => {
    const file = e.target.files[0];
    if (file) {
      setVideoUrl(URL.createObjectURL(file));
      setFileName(file.name.replace(/\.[^.]+$/, ''));
      setError('');
      setSelection(null);
      setExtractedFrames([]);
    }
  };

  // 保存当前帧为图片
  const handleSaveFrame = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(blob => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${fileName || 'frame'}_${Math.floor(video.currentTime * 1000)}ms.png`;
      a.click();
    }, 'image/png');
  };

  // 功能2：抽帧方式相关函数
  
  // 等间隔抽帧
  const handleIntervalExtract = async () => {
    if (!videoRef.current || isExtracting) return;
    
    setIsExtracting(true);
    setExtractProgress(0);
    setExtractedFrames([]);
    
    const video = videoRef.current;
    const frames = [];
    
    try {
      // 计算抽帧时间点
      const timePoints = [];
      if (intervalType === 'seconds') {
        for (let time = 0; time <= duration; time += intervalValue) {
          timePoints.push(time);
        }
      } else {
        // 按帧数抽帧
        const totalFrames = Math.floor(duration * 30); // 假设30fps
        const frameInterval = Math.floor(totalFrames / intervalValue);
        for (let i = 0; i < totalFrames; i += frameInterval) {
          timePoints.push(i / 30);
        }
      }
      
      // 逐个抽帧
      for (let i = 0; i < timePoints.length; i++) {
        const time = timePoints[i];
        video.currentTime = time;
        
        // 等待视频seek完成
        await new Promise(resolve => {
          const onSeeked = () => {
            video.removeEventListener('seeked', onSeeked);
            resolve();
          };
          video.addEventListener('seeked', onSeeked);
        });
        
        // 截取当前帧
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        canvas.toBlob(blob => {
          const frame = {
            id: i,
            time: time,
            blob: blob,
            url: URL.createObjectURL(blob),
            name: `${fileName || 'frame'}_${Math.floor(time * 1000)}ms.png`
          };
          frames.push(frame);
          setExtractedFrames([...frames]);
        }, 'image/png');
        
        setExtractProgress((i + 1) / timePoints.length * 100);
      }
    } catch (error) {
      setError('抽帧过程中出现错误：' + error.message);
    } finally {
      setIsExtracting(false);
    }
  };

  // 自定义时间点抽帧
  const handleCustomExtract = async () => {
    if (!videoRef.current || isExtracting) return;
    
    const times = customTimes.split(',').map(t => parseFloat(t.trim())).filter(t => !isNaN(t) && t >= 0 && t <= duration);
    if (times.length === 0) {
      setError('请输入有效的时间点');
      return;
    }
    
    setIsExtracting(true);
    setExtractProgress(0);
    setExtractedFrames([]);
    
    const video = videoRef.current;
    const frames = [];
    
    try {
      for (let i = 0; i < times.length; i++) {
        const time = times[i];
        video.currentTime = time;
        
        await new Promise(resolve => {
          const onSeeked = () => {
            video.removeEventListener('seeked', onSeeked);
            resolve();
          };
          video.addEventListener('seeked', onSeeked);
        });
        
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        canvas.toBlob(blob => {
          const frame = {
            id: i,
            time: time,
            blob: blob,
            url: URL.createObjectURL(blob),
            name: `${fileName || 'frame'}_${Math.floor(time * 1000)}ms.png`
          };
          frames.push(frame);
          setExtractedFrames([...frames]);
        }, 'image/png');
        
        setExtractProgress((i + 1) / times.length * 100);
      }
    } catch (error) {
      setError('抽帧过程中出现错误：' + error.message);
    } finally {
      setIsExtracting(false);
    }
  };

  // 区间抽帧
  const handleRangeExtract = async () => {
    if (!videoRef.current || isExtracting) return;
    
    if (rangeStart >= rangeEnd) {
      setError('开始时间必须小于结束时间');
      return;
    }
    
    setIsExtracting(true);
    setExtractProgress(0);
    setExtractedFrames([]);
    
    const video = videoRef.current;
    const frames = [];
    
    try {
      const timePoints = [];
      for (let time = rangeStart; time <= rangeEnd; time += intervalValue) {
        timePoints.push(time);
      }
      
      for (let i = 0; i < timePoints.length; i++) {
        const time = timePoints[i];
        video.currentTime = time;
        
        await new Promise(resolve => {
          const onSeeked = () => {
            video.removeEventListener('seeked', onSeeked);
            resolve();
          };
          video.addEventListener('seeked', onSeeked);
        });
        
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        canvas.toBlob(blob => {
          const frame = {
            id: i,
            time: time,
            blob: blob,
            url: URL.createObjectURL(blob),
            name: `${fileName || 'frame'}_${Math.floor(time * 1000)}ms.png`
          };
          frames.push(frame);
          setExtractedFrames([...frames]);
        }, 'image/png');
        
        setExtractProgress((i + 1) / timePoints.length * 100);
      }
    } catch (error) {
      setError('抽帧过程中出现错误：' + error.message);
    } finally {
      setIsExtracting(false);
    }
  };

  // 关键帧提取（简化版本，按固定间隔提取）
  const handleKeyframeExtract = async () => {
    if (!videoRef.current || isExtracting) return;
    
    setIsExtracting(true);
    setExtractProgress(0);
    setExtractedFrames([]);
    
    const video = videoRef.current;
    const frames = [];
    
    try {
      // 简化版本：按1秒间隔提取，实际应该检测I帧
      const timePoints = [];
      for (let time = 0; time <= duration; time += 1) {
        timePoints.push(time);
      }
      
      for (let i = 0; i < timePoints.length; i++) {
        const time = timePoints[i];
        video.currentTime = time;
        
        await new Promise(resolve => {
          const onSeeked = () => {
            video.removeEventListener('seeked', onSeeked);
            resolve();
          };
          video.addEventListener('seeked', onSeeked);
        });
        
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        canvas.toBlob(blob => {
          const frame = {
            id: i,
            time: time,
            blob: blob,
            url: URL.createObjectURL(blob),
            name: `${fileName || 'keyframe'}_${Math.floor(time * 1000)}ms.png`
          };
          frames.push(frame);
          setExtractedFrames([...frames]);
        }, 'image/png');
        
        setExtractProgress((i + 1) / timePoints.length * 100);
      }
    } catch (error) {
      setError('关键帧提取过程中出现错误：' + error.message);
    } finally {
      setIsExtracting(false);
    }
  };

  // 开始抽帧
  const handleStartExtract = () => {
    switch (extractMode) {
      case 'interval':
        handleIntervalExtract();
        break;
      case 'custom':
        handleCustomExtract();
        break;
      case 'range':
        handleRangeExtract();
        break;
      case 'keyframe':
        handleKeyframeExtract();
        break;
      default:
        break;
    }
  };

  // 清除抽帧结果
  const handleClearFrames = () => {
    setExtractedFrames([]);
    setExtractProgress(0);
    setSelectedFrames(new Set());
    setPreviewFrame(null);
    setShowPreview(false);
  };

  // 功能3：帧预览与管理相关函数
  
  // 选择/取消选择帧
  const handleFrameSelect = (frameId) => {
    const newSelected = new Set(selectedFrames);
    if (newSelected.has(frameId)) {
      newSelected.delete(frameId);
    } else {
      newSelected.add(frameId);
    }
    setSelectedFrames(newSelected);
  };

  // 全选/取消全选
  const handleSelectAll = () => {
    if (selectedFrames.size === extractedFrames.length) {
      setSelectedFrames(new Set());
    } else {
      setSelectedFrames(new Set(extractedFrames.map(f => f.id)));
    }
  };

  // 反选
  const handleInvertSelection = () => {
    const newSelected = new Set();
    extractedFrames.forEach(frame => {
      if (!selectedFrames.has(frame.id)) {
        newSelected.add(frame.id);
      }
    });
    setSelectedFrames(newSelected);
  };

  // 删除选中的帧
  const handleDeleteSelected = () => {
    const newFrames = extractedFrames.filter(frame => !selectedFrames.has(frame.id));
    setExtractedFrames(newFrames);
    setSelectedFrames(new Set());
  };

  // 预览帧
  const handlePreviewFrame = (frame) => {
    setPreviewFrame(frame);
    setShowPreview(true);
  };

  // 关闭预览
  const handleClosePreview = () => {
    setShowPreview(false);
    setPreviewFrame(null);
  };

  // 开始重命名帧
  const handleStartRename = (frame) => {
    setFrameRenameId(frame.id);
    setFrameRenameValue(frame.name);
  };

  // 确认重命名
  const handleConfirmRename = () => {
    if (frameRenameValue.trim()) {
      const newFrames = extractedFrames.map(frame => 
        frame.id === frameRenameId 
          ? { ...frame, name: frameRenameValue.trim() }
          : frame
      );
      setExtractedFrames(newFrames);
    }
    setFrameRenameId(null);
    setFrameRenameValue('');
  };

  // 取消重命名
  const handleCancelRename = () => {
    setFrameRenameId(null);
    setFrameRenameValue('');
  };

  // 排序帧
  const handleSortFrames = (sortBy) => {
    if (frameSortBy === sortBy) {
      setFrameSortOrder(frameSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setFrameSortBy(sortBy);
      setFrameSortOrder('asc');
    }
  };

  // 获取排序后的帧列表
  const getSortedFrames = () => {
    const sorted = [...extractedFrames].sort((a, b) => {
      let aValue, bValue;
      
      switch (frameSortBy) {
        case 'time':
          aValue = a.time;
          bValue = b.time;
          break;
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'id':
        default:
          aValue = a.id;
          bValue = b.id;
          break;
      }
      
      if (aValue < bValue) return frameSortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return frameSortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  };

  // 下载单个帧
  const handleDownloadFrame = (frame) => {
    const a = document.createElement('a');
    a.href = frame.url;
    a.download = frame.name;
    a.click();
  };

  // 保存选区为图片
  const handleSaveSelection = () => {
    const video = videoRef.current;
    if (!video || !selection) return;
    // 获取视频实际像素和显示像素的比例
    const videoRect = video.getBoundingClientRect();
    const overlayRect = overlayRef.current.getBoundingClientRect();
    const scaleX = video.videoWidth / videoRect.width;
    const scaleY = video.videoHeight / videoRect.height;
    // 计算选区在视频内容上的实际像素坐标
    const offsetX = videoRect.left - overlayRect.left;
    const offsetY = videoRect.top - overlayRect.top;
    const x1 = selection.startX;
    const y1 = selection.startY;
    const x2 = selection.endX;
    const y2 = selection.endY;
    const sx = Math.round((Math.min(x1, x2) - offsetX) * scaleX);
    const sy = Math.round((Math.min(y1, y2) - offsetY) * scaleY);
    const sw = Math.round(Math.abs(x2 - x1) * scaleX);
    const sh = Math.round(Math.abs(y2 - y1) * scaleY);
    // 限制不超出视频实际像素
    const safeSx = Math.max(0, Math.min(sx, video.videoWidth - 1));
    const safeSy = Math.max(0, Math.min(sy, video.videoHeight - 1));
    const safeSw = Math.max(1, Math.min(sw, video.videoWidth - safeSx));
    const safeSh = Math.max(1, Math.min(sh, video.videoHeight - safeSy));
    const canvas = document.createElement('canvas');
    canvas.width = safeSw;
    canvas.height = safeSh;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, safeSx, safeSy, safeSw, safeSh, 0, 0, safeSw, safeSh);
    canvas.toBlob(blob => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${fileName || 'frame'}_${Math.floor(video.currentTime * 1000)}ms_crop.png`;
      a.click();
    }, 'image/png');
  };

  // 拖拽上传
  const handleDrop = e => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      inputRef.current.files = e.dataTransfer.files;
      handleFile({ target: { files: e.dataTransfer.files } });
    }
  };

  // 进度条和播放控制
  const handleTimeChange = e => {
    const t = parseFloat(e.target.value);
    setCurrentTime(t);
    if (videoRef.current) videoRef.current.currentTime = t;
  };
  const handlePlayPause = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
    } else {
      videoRef.current.pause();
    }
  };

  // 事件绑定 - 使用直接DOM操作，确保零延时
  const handleOverlayMouseDown = (e) => {
    const state = currentState.current;
    if (state.selecting || state.dragging || state.resizing) return;
    
    const rect = overlayRef.current.getBoundingClientRect();
    let startX = e.clientX - rect.left;
    let startY = e.clientY - rect.top;
    // 限制起点在区域内
    startX = Math.max(0, Math.min(startX, rect.width));
    startY = Math.max(0, Math.min(startY, rect.height));
    
    // 立即更新状态
    currentState.current.selecting = true;
    const newSelection = { startX, startY, endX: startX, endY: startY };
    currentState.current.selection = newSelection;
    
    setSelecting(true);
    setSelection(newSelection);
  };

  const handleOverlayMouseMove = (e) => {
    const state = currentState.current;
    
    if (state.selecting) {
      const rect = overlayRef.current.getBoundingClientRect();
      let endX = e.clientX - rect.left;
      let endY = e.clientY - rect.top;
      // 限制终点不超出区域
      endX = Math.max(0, Math.min(endX, rect.width));
      endY = Math.max(0, Math.min(endY, rect.height));
      
      // 立即更新状态
      if (state.selection) {
        state.selection.endX = endX;
        state.selection.endY = endY;
        
        // 使用requestAnimationFrame确保流畅更新
        requestAnimationFrame(() => {
          setSelection({ ...state.selection });
        });
      }
    } else if (state.dragging && state.selection) {
      const rect = overlayRef.current.getBoundingClientRect();
      const width = Math.abs(state.selection.endX - state.selection.startX);
      const height = Math.abs(state.selection.endY - state.selection.startY);
      
      // 计算新左上角
      let nx = e.clientX - rect.left - state.dragOffset.x;
      let ny = e.clientY - rect.top - state.dragOffset.y;
      // 限制不超出区域
      nx = Math.max(0, Math.min(nx, rect.width - width));
      ny = Math.max(0, Math.min(ny, rect.height - height));
      
      // 计算新右下角
      let ex = nx + width;
      let ey = ny + height;
      
      // 保持方向并立即更新
      let newSelection;
      if (state.selection.startX <= state.selection.endX && state.selection.startY <= state.selection.endY) {
        newSelection = { startX: nx, startY: ny, endX: ex, endY: ey };
      } else if (state.selection.startX > state.selection.endX && state.selection.startY <= state.selection.endY) {
        newSelection = { startX: ex, startY: ny, endX: nx, endY: ey };
      } else if (state.selection.startX <= state.selection.endX && state.selection.startY > state.selection.endY) {
        newSelection = { startX: nx, startY: ey, endX: ex, endY: ny };
      } else {
        newSelection = { startX: ex, startY: ey, endX: nx, endY: ny };
      }
      
      // 立即更新状态
      currentState.current.selection = newSelection;
      
      // 使用requestAnimationFrame确保流畅更新
      requestAnimationFrame(() => {
        setSelection(newSelection);
      });
    } else if (state.resizing && state.selection && state.resizeCorner) {
      const rect = overlayRef.current.getBoundingClientRect();
      let mouseX = e.clientX - rect.left;
      let mouseY = e.clientY - rect.top;
      
      // 限制鼠标位置在区域内
      mouseX = Math.max(0, Math.min(mouseX, rect.width));
      mouseY = Math.max(0, Math.min(mouseY, rect.height));
      
      let newSelection = { ...state.selection };
      const minSize = 20; // 最小选区大小
      
      switch (state.resizeCorner) {
        case 'nw':
          newSelection.startX = Math.min(mouseX, newSelection.endX - minSize);
          newSelection.startY = Math.min(mouseY, newSelection.endY - minSize);
          break;
        case 'ne':
          newSelection.endX = Math.max(mouseX, newSelection.startX + minSize);
          newSelection.startY = Math.min(mouseY, newSelection.endY - minSize);
          break;
        case 'sw':
          newSelection.startX = Math.min(mouseX, newSelection.endX - minSize);
          newSelection.endY = Math.max(mouseY, newSelection.startY + minSize);
          break;
        case 'se':
          newSelection.endX = Math.max(mouseX, newSelection.startX + minSize);
          newSelection.endY = Math.max(mouseY, newSelection.startY + minSize);
          break;
      }
      
      // 立即更新状态
      currentState.current.selection = newSelection;
      
      // 使用requestAnimationFrame确保流畅更新
      requestAnimationFrame(() => {
        setSelection(newSelection);
      });
    }
  };

  const handleOverlayMouseUp = () => {
    currentState.current.selecting = false;
    currentState.current.dragging = false;
    currentState.current.resizing = false;
    setSelecting(false);
    setDragging(false);
    setResizing(false);
    setResizeCorner(null);
  };

  const handleSelectionMouseDown = (e) => {
    e.stopPropagation();
    currentState.current.dragging = true;
    const newDragOffset = {
      x: e.nativeEvent.offsetX,
      y: e.nativeEvent.offsetY
    };
    currentState.current.dragOffset = newDragOffset;
    setDragging(true);
    setDragOffset(newDragOffset);
  };

  const handleCornerMouseDown = (e, corner) => {
    e.stopPropagation();
    currentState.current.resizing = true;
    currentState.current.resizeCorner = corner;
    setResizing(true);
    setResizeCorner(corner);
  };

  // 功能4：批量导出相关
  // 批量导出图片为zip
  const handleBatchDownloadZip = async (onlySelected = false) => {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    const frames = onlySelected
      ? extractedFrames.filter(f => selectedFrames.has(f.id))
      : extractedFrames;
    if (frames.length === 0) return;
    for (const frame of frames) {
      zip.file(frame.name, frame.blob);
    }
    const blob = await zip.generateAsync({ type: 'blob' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = onlySelected ? 'selected_frames.zip' : 'all_frames.zip';
    a.click();
  };

  // 批量导出为GIF（使用gif.js）
  const handleBatchDownloadGIF = async (onlySelected = false) => {
    try {
      const frames = onlySelected
        ? extractedFrames.filter(f => selectedFrames.has(f.id))
        : extractedFrames;
      if (frames.length === 0) return;
      
      // 使用gif.js
      try {
        const GIF = (await import('gif.js')).default;
        
        // 获取第一帧的尺寸
        const firstFrame = frames[0];
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = firstFrame.url;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
        
        const gif = new GIF({ 
          workers: 1, 
          quality: 10,
          width: img.width,
          height: img.height,
          workerScript: '/gif.js.worker.js'
        });
        
        for (const frame of frames) {
          const frameImg = new Image();
          frameImg.crossOrigin = 'anonymous';
          frameImg.src = frame.url;
          await new Promise((resolve, reject) => {
            frameImg.onload = resolve;
            frameImg.onerror = reject;
          });
          
          gif.addFrame(frameImg, { delay: 200 });
        }
        
        gif.on('finished', function(blob) {
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = onlySelected ? 'selected_frames.gif' : 'all_frames.gif';
          a.click();
          URL.revokeObjectURL(a.href);
        });
        
        gif.render();
        
      } catch (gifError) {
        console.warn('gif.js失败，使用备选方案:', gifError);
        
        // 备选方案：使用Canvas创建简单的动画
        await createSimpleAnimation(frames, onlySelected);
      }
    } catch (error) {
      console.error('GIF导出失败:', error);
      alert('GIF导出失败: ' + error.message);
    }
  };

  // 备选方案：使用Canvas创建简单的动画
  const createSimpleAnimation = async (frames, onlySelected) => {
    if (frames.length === 0) return;
    
    try {
      // 尝试使用gif.js.optimized作为备选
      const GIF = (await import('gif.js.optimized')).default;
      
      // 获取第一帧的尺寸
      const firstFrame = frames[0];
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = firstFrame.url;
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
      
      const gif = new GIF({ 
        workers: 1, 
        quality: 10,
        workerScript: '/gif.worker.js'
      });
      
      for (const frame of frames) {
        const frameImg = new Image();
        frameImg.crossOrigin = 'anonymous';
        frameImg.src = frame.url;
        await new Promise((resolve, reject) => {
          frameImg.onload = resolve;
          frameImg.onerror = reject;
        });
        
        gif.addFrame(frameImg, { delay: 200 });
      }
      
      gif.on('finished', function(blob) {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = onlySelected ? 'selected_frames.gif' : 'all_frames.gif';
        a.click();
        URL.revokeObjectURL(a.href);
      });
      
      gif.render();
      
    } catch (error) {
      console.warn('所有GIF库都失败，导出为PNG序列:', error);
      
      // 最后的备选方案：导出为PNG序列
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      const firstFrame = frames[0];
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = firstFrame.url;
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
      
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      
      canvas.toBlob(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = onlySelected ? 'selected_frames_animation.png' : 'all_frames_animation.png';
        a.click();
        URL.revokeObjectURL(a.href);
      }, 'image/png');
    }
  };

  return (
    <div className="video-frame-extractor-container" onDrop={handleDrop} onDragOver={e => e.preventDefault()}>
      {/* 操作说明：全宽展示，最顶部 */}
      {videoUrl && (
        <div className="video-frame-extractor-tips-bar">
          <div className="video-frame-extractor-tips-title">
            操作说明
          </div>
          <div className="video-frame-extractor-tips">
            <div className="video-frame-extractor-tip">拖动进度条选择时间点</div>
            <div className="video-frame-extractor-tip">暂停后可在视频上框选区域</div>
            <div className="video-frame-extractor-tip">拖拽选区中心可移动位置</div>
            <div className="video-frame-extractor-tip">拖拽四角绿点可调整选区大小</div>
            <div className="video-frame-extractor-tip">框选区域后点击保存选区</div>
            <div className="video-frame-extractor-tip">选择抽帧方式批量提取帧</div>
          </div>
        </div>
      )}
      <div className="video-frame-extractor-upload">
        <input
          type="file"
          accept="video/*"
          ref={inputRef}
          onChange={handleFile}
          className="video-frame-extractor-input"
        />
        <span className="video-frame-extractor-upload-tip">点击或拖拽上传本地视频</span>
      </div>
      {error && <div className="video-frame-extractor-error">{error}</div>}
      {videoUrl && (
        <div className="video-frame-extractor-main-content">
          {/* 左侧视频和控制区域 */}
          <div className="video-frame-extractor-video-section">
            <div className="video-frame-extractor-video-container">
              <video
                ref={videoRef}
                src={videoUrl}
                className="video-frame-extractor-video"
                onTimeUpdate={e => setCurrentTime(e.target.currentTime)}
                onLoadedMetadata={e => setDuration(e.target.duration)}
              />
              {/* 框选区域 */}
              <div
                ref={overlayRef}
                className="video-frame-extractor-overlay"
                onMouseDown={handleOverlayMouseDown}
                onMouseMove={handleOverlayMouseMove}
                onMouseUp={handleOverlayMouseUp}
              >
                <div
                  ref={selectionRef}
                  className="video-frame-extractor-selection"
                  style={{
                    position: 'absolute',
                    border: '2px solid #00ff00',
                    background: 'rgba(0,255,0,0.08)',
                    cursor: dragging ? 'move' : 'pointer',
                    zIndex: 3,
                    display: 'none'
                  }}
                  onMouseDown={handleSelectionMouseDown}
                >
                  {/* 四角拖拽点 */}
                  {['nw','ne','sw','se'].map(corner => {
                    const style = {
                      position: 'absolute',
                      width: 12,
                      height: 12,
                      background: '#00ff00',
                      borderRadius: 6,
                      zIndex: 4,
                      cursor: `${corner}-resize`,
                      boxShadow: '0 0 8px #00ff00'
                    };
                    if (corner === 'nw') Object.assign(style, { left: -6, top: -6 });
                    if (corner === 'ne') Object.assign(style, { right: -6, top: -6 });
                    if (corner === 'sw') Object.assign(style, { left: -6, bottom: -6 });
                    if (corner === 'se') Object.assign(style, { right: -6, bottom: -6 });
                    return (
                      <div 
                        key={corner} 
                        data-corner={corner} 
                        className="video-frame-extractor-corner" 
                        style={style} 
                        onMouseDown={(e) => handleCornerMouseDown(e, corner)} 
                      />
                    );
                  })}
                </div>
              </div>
            </div>
            {/* 播放控制区 */}
            <div className="video-frame-extractor-controls">
              <Button onClick={handlePlayPause}>
                {videoRef.current && videoRef.current.paused ? '播放' : '暂停'}
              </Button>
              <input
                type="range"
                min={0}
                max={duration}
                step={0.01}
                value={currentTime}
                onChange={handleTimeChange}
                className="video-frame-extractor-slider"
              />
              <span className="video-frame-extractor-time-display">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>
          </div>
          
          {/* 右侧操作区域 */}
          <div className="video-frame-extractor-actions-section">
            {/* 功能2：抽帧方式选择 */}
            <div className="video-frame-extractor-extract-section">
              <div className="video-frame-extractor-extract-title">
                <strong>抽帧方式</strong>
              </div>
              
              {/* 抽帧模式选择 */}
              <div className="video-frame-extractor-mode-selector">
                <label className="video-frame-extractor-mode-option">
                  <input
                    type="radio"
                    name="extractMode"
                    value="manual"
                    checked={extractMode === 'manual'}
                    onChange={(e) => setExtractMode(e.target.value)}
                  />
                  <span>手动选帧</span>
                </label>
                <label className="video-frame-extractor-mode-option">
                  <input
                    type="radio"
                    name="extractMode"
                    value="interval"
                    checked={extractMode === 'interval'}
                    onChange={(e) => setExtractMode(e.target.value)}
                  />
                  <span>等间隔抽帧</span>
                </label>
                <label className="video-frame-extractor-mode-option">
                  <input
                    type="radio"
                    name="extractMode"
                    value="keyframe"
                    checked={extractMode === 'keyframe'}
                    onChange={(e) => setExtractMode(e.target.value)}
                  />
                  <span>关键帧提取</span>
                </label>
                <label className="video-frame-extractor-mode-option">
                  <input
                    type="radio"
                    name="extractMode"
                    value="custom"
                    checked={extractMode === 'custom'}
                    onChange={(e) => setExtractMode(e.target.value)}
                  />
                  <span>自定义时间点</span>
                </label>
                <label className="video-frame-extractor-mode-option">
                  <input
                    type="radio"
                    name="extractMode"
                    value="range"
                    checked={extractMode === 'range'}
                    onChange={(e) => setExtractMode(e.target.value)}
                  />
                  <span>区间抽帧</span>
                </label>
              </div>

              {/* 等间隔抽帧参数 */}
              {extractMode === 'interval' && (
                <div className="video-frame-extractor-params">
                  <div className="video-frame-extractor-param-group">
                    <label>间隔类型：</label>
                    <select 
                      value={intervalType} 
                      onChange={(e) => setIntervalType(e.target.value)}
                      className="video-frame-extractor-select"
                    >
                      <option value="seconds">按秒数</option>
                      <option value="frames">按帧数</option>
                    </select>
                  </div>
                  <div className="video-frame-extractor-param-group">
                    <label>间隔值：</label>
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={intervalValue}
                      onChange={(e) => setIntervalValue(parseFloat(e.target.value))}
                      className="video-frame-extractor-input-number"
                    />
                    <span>{intervalType === 'seconds' ? '秒' : '帧'}</span>
                  </div>
                </div>
              )}

              {/* 自定义时间点参数 */}
              {extractMode === 'custom' && (
                <div className="video-frame-extractor-params">
                  <div className="video-frame-extractor-param-group">
                    <label>时间点（秒，用逗号分隔）：</label>
                    <input
                      type="text"
                      value={customTimes}
                      onChange={(e) => setCustomTimes(e.target.value)}
                      placeholder="0, 5, 10, 15"
                      className="video-frame-extractor-input-text"
                    />
                  </div>
                </div>
              )}

              {/* 区间抽帧参数 */}
              {extractMode === 'range' && (
                <div className="video-frame-extractor-params">
                  <div className="video-frame-extractor-param-group">
                    <label>开始时间：</label>
                    <input
                      type="number"
                      min="0"
                      max={duration}
                      step="0.1"
                      value={rangeStart}
                      onChange={(e) => setRangeStart(parseFloat(e.target.value))}
                      className="video-frame-extractor-input-number"
                    />
                    <span>秒</span>
                  </div>
                  <div className="video-frame-extractor-param-group">
                    <label>结束时间：</label>
                    <input
                      type="number"
                      min="0"
                      max={duration}
                      step="0.1"
                      value={rangeEnd}
                      onChange={(e) => setRangeEnd(parseFloat(e.target.value))}
                      className="video-frame-extractor-input-number"
                    />
                    <span>秒</span>
                  </div>
                  <div className="video-frame-extractor-param-group">
                    <label>间隔：</label>
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={intervalValue}
                      onChange={(e) => setIntervalValue(parseFloat(e.target.value))}
                      className="video-frame-extractor-input-number"
                    />
                    <span>秒</span>
                  </div>
                </div>
              )}

              {/* 抽帧控制按钮 */}
              {extractMode !== 'manual' && (
                <div className="video-frame-extractor-extract-controls">
                  <Button 
                    type="primary" 
                    onClick={handleStartExtract}
                    disabled={isExtracting}
                  >
                    {isExtracting ? '抽帧中...' : '开始抽帧'}
                  </Button>
                  <Button 
                    onClick={handleClearFrames}
                    disabled={extractedFrames.length === 0}
                  >
                    清除结果
                  </Button>
                </div>
              )}

              {/* 抽帧进度 */}
              {isExtracting && (
                <ProgressBar percent={extractProgress} label={Math.round(extractProgress) + '%'} height={10} />
              )}

              {/* 抽帧结果统计 */}
              {extractedFrames.length > 0 && (
                <div className="video-frame-extractor-stats">
                  <span>已抽取 {extractedFrames.length} 帧</span>
                </div>
              )}

              {/* 手动操作按钮 */}
              <div className="video-frame-extractor-actions">
                <Button onClick={handleSaveFrame}>
                  保存当前帧为图片
                </Button>
                <Button 
                  onClick={handleSaveSelection} 
                  disabled={!selection || Math.abs(selection.endX - selection.startX) < 5 || Math.abs(selection.endY - selection.startY) < 5}
                >
                  保存选区为图片
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 帧预览与管理模块：全宽展示，只出现一次 */}
      {videoUrl && extractedFrames.length > 0 && (
        <div className="video-frame-extractor-frames-section-fullwidth">
          <div className="video-frame-extractor-frames-section">
            <div className="video-frame-extractor-frames-header">
              <div className="video-frame-extractor-frames-title">
                <strong>帧预览与管理</strong>
              </div>
              <div className="video-frame-extractor-display-controls">
                <button
                  className={`video-frame-extractor-display-btn ${frameDisplayMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setFrameDisplayMode('grid')}
                  title="网格显示"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <rect x="2" y="2" width="4" height="4" rx="1"/>
                    <rect x="10" y="2" width="4" height="4" rx="1"/>
                    <rect x="2" y="10" width="4" height="4" rx="1"/>
                    <rect x="10" y="10" width="4" height="4" rx="1"/>
                  </svg>
                </button>
                <button
                  className={`video-frame-extractor-display-btn ${frameDisplayMode === 'list' ? 'active' : ''}`}
                  onClick={() => setFrameDisplayMode('list')}
                  title="列表显示"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <rect x="2" y="2" width="12" height="2" rx="1"/>
                    <rect x="2" y="7" width="12" height="2" rx="1"/>
                    <rect x="2" y="12" width="12" height="2" rx="1"/>
                  </svg>
                </button>
              </div>
            </div>
            <div className="video-frame-extractor-batch-toolbar">
              <div className="video-frame-extractor-batch-info">
                <span>已选择 {selectedFrames.size} / {extractedFrames.length} 帧</span>
              </div>
              <div className="video-frame-extractor-batch-actions">
                <Button size="small" onClick={handleSelectAll}>
                  {selectedFrames.size === extractedFrames.length ? '取消全选' : '全选'}
                </Button>
                <Button size="small" onClick={handleInvertSelection}>反选</Button>
                <Button size="small" type="danger" onClick={handleDeleteSelected} disabled={selectedFrames.size === 0}>删除选中</Button>
                <Button size="small" onClick={() => handleBatchDownloadZip(false)} disabled={extractedFrames.length === 0}>导出全部为Zip</Button>
                <Button size="small" onClick={() => handleBatchDownloadZip(true)} disabled={selectedFrames.size === 0}>导出选中为Zip</Button>
                <Button size="small" onClick={() => handleBatchDownloadGIF(false)} disabled={extractedFrames.length === 0}>导出全部为GIF</Button>
                <Button size="small" onClick={() => handleBatchDownloadGIF(true)} disabled={selectedFrames.size === 0}>导出选中为GIF</Button>
              </div>
            </div>
            <div className="video-frame-extractor-sort-toolbar">
              <span>排序：</span>
              <button
                className={`video-frame-extractor-sort-btn ${frameSortBy === 'time' ? 'active' : ''}`}
                onClick={() => handleSortFrames('time')}
              >
                时间
                {frameSortBy === 'time' && (
                  <span className="video-frame-extractor-sort-arrow">
                    {frameSortOrder === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </button>
              <button
                className={`video-frame-extractor-sort-btn ${frameSortBy === 'name' ? 'active' : ''}`}
                onClick={() => handleSortFrames('name')}
              >
                名称
                {frameSortBy === 'name' && (
                  <span className="video-frame-extractor-sort-arrow">
                    {frameSortOrder === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </button>
              <button
                className={`video-frame-extractor-sort-btn ${frameSortBy === 'id' ? 'active' : ''}`}
                onClick={() => handleSortFrames('id')}
              >
                序号
                {frameSortBy === 'id' && (
                  <span className="video-frame-extractor-sort-arrow">
                    {frameSortOrder === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </button>
            </div>
            <div className={`video-frame-extractor-frames-container ${frameDisplayMode}`}>
              {getSortedFrames().map(frame => (
                <div
                  key={frame.id}
                  className={`video-frame-extractor-frame-item ${selectedFrames.has(frame.id) ? 'selected' : ''}`}
                >
                  {/* 选择框 */}
                  <div className="video-frame-extractor-frame-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedFrames.has(frame.id)}
                      onChange={() => handleFrameSelect(frame.id)}
                    />
                  </div>
                  {/* 帧缩略图 */}
                  <div 
                    className="video-frame-extractor-frame-thumbnail"
                    onClick={() => handlePreviewFrame(frame)}
                  >
                    <img src={frame.url} alt={frame.name} />
                    <div className="video-frame-extractor-frame-overlay">
                      <span>点击预览</span>
                    </div>
                  </div>
                  {/* 帧信息 */}
                  <div className="video-frame-extractor-frame-info">
                    {frameRenameId === frame.id ? (
                      <div className="video-frame-extractor-frame-rename">
                        <input
                          type="text"
                          value={frameRenameValue}
                          onChange={(e) => setFrameRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleConfirmRename();
                            if (e.key === 'Escape') handleCancelRename();
                          }}
                          className="video-frame-extractor-rename-input"
                          autoFocus
                        />
                        <Button
                          onClick={handleConfirmRename}
                          title="确认"
                        >
                          ✓
                        </Button>
                        <Button
                          onClick={handleCancelRename}
                          title="取消"
                        >
                          ✕
                        </Button>
                      </div>
                    ) : (
                      <div className="video-frame-extractor-frame-name">
                        {frame.name}
                      </div>
                    )}
                    <div className="video-frame-extractor-frame-time">
                      {formatTime(frame.time)}
                    </div>
                  </div>
                  {/* 操作按钮 */}
                  <div className="video-frame-extractor-frame-actions">
                    <Button
                      onClick={() => handleStartRename(frame)}
                      title="重命名"
                    >
                      ✏️
                    </Button>
                    <Button
                      onClick={() => handleDownloadFrame(frame)}
                      title="下载"
                    >
                      ⬇️
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 图片预览弹窗，使用通用组件 */}
      <ImagePreviewModal
        show={showPreview}
        image={previewFrame?.url}
        title={previewFrame?.name}
        info={previewFrame ? [
          `时间点：${formatTime(previewFrame.time)}`,
          `文件名：${previewFrame.name}`
        ] : []}
        onClose={handleClosePreview}
        onDownload={previewFrame ? () => handleDownloadFrame(previewFrame) : undefined}
      />
    </div>
  );
}

function formatTime(t) {
  if (!t || isNaN(t)) return '00:00';
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
} 